// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

import { type Dispatch, type SetStateAction, useEffect, useRef } from 'react'

/**
 * Acknowledgements shared between the alert surfaces of ONE tab (vic/vetpo#42,
 * VP-ALERTS R39).
 *
 * The app-bar bell and the alert panel poll alerts-since independently, and
 * that feed returns rows by triggeredAt: an alert acknowledged in one surface
 * is never re-read by the other, which kept counting it for the session.
 *
 * So the tab keeps a RECORD of every acknowledge the SERVER ACCEPTED, and every
 * surface applies it -- on the event, and again on every merge of a poll. A
 * one-shot event alone lost the ack whenever the receiver had not listed the
 * alert yet (a panel mounting as the bell's click navigates to it) or a poll's
 * merge from an older snapshot landed after it (review of #42).
 *
 * Recorded on success only, never on the optimistic update: an acknowledge the
 * server then refuses would otherwise show as resolved in the other surface --
 * the wrong direction for a hypo alert. The server never clears an
 * acknowledgedAt, so a recorded one stays true. Same tab only: another tab,
 * another vet or the owner's app still never reaches an open surface (§7).
 */
const ALERT_ACKNOWLEDGED = 'vetpo:alert-acknowledged'

type AlertId = number | string
type Ackable = { id: AlertId; acknowledgedAt?: string | null }

const known = new Map<AlertId, string>()

/** The server accepted an acknowledge: record it and tell this tab's surfaces. */
export const announceAlertAcknowledged = (alertId: AlertId, acknowledgedAt: string): void => {
  known.set(alertId, acknowledgedAt)
  window.dispatchEvent(new CustomEvent(ALERT_ACKNOWLEDGED, { detail: { alertId } }))
}

/** Another surface's acknowledge of `alertId` succeeded (a failed one must not revert it). */
export const isKnownAcknowledged = (alertId: AlertId): boolean => known.has(alertId)

/**
 * Apply every recorded acknowledge to `list`. Returns the SAME array when none
 * applies, so a no-op does not re-render.
 */
export const withKnownAcks = <T extends Ackable>(list: T[]): T[] => {
  if (!list.some((a) => !a.acknowledgedAt && known.has(a.id))) return list
  return list.map((a) =>
    !a.acknowledgedAt && known.has(a.id) ? { ...a, acknowledgedAt: known.get(a.id) } : a,
  )
}

/**
 * Keep a surface's list in step with this tab's acknowledgements. `onAcknowledged`
 * runs after each one (the all-pets surfaces re-poll the server total there,
 * rather than subtracting from a total that may already be newer).
 */
export const useAcknowledgementsFromOtherSurfaces = <T extends Ackable>(
  setAlerts: Dispatch<SetStateAction<T[]>>,
  onAcknowledged?: () => void,
): void => {
  const onAckRef = useRef(onAcknowledged)
  onAckRef.current = onAcknowledged
  // biome-ignore lint/correctness/useExhaustiveDependencies: subscribe once; the setter is stable and the callback is read through a ref
  useEffect(() => {
    const listener = () => {
      setAlerts((prev) => withKnownAcks(prev))
      onAckRef.current?.()
    }
    window.addEventListener(ALERT_ACKNOWLEDGED, listener)
    return () => window.removeEventListener(ALERT_ACKNOWLEDGED, listener)
  }, [])
}

/**
 * Acknowledges still IN FLIGHT in this tab, by alert (a count: the bell and the
 * panel can both be acknowledging one alert), and the desktop notification a
 * poll owes each of them (VP-ALERTS R45, AckSurfaces.tla K1b).
 *
 * A read served before an acknowledge commits still carries the alert
 * unacknowledged. Popping a notification for the alert the vet just clicked is
 * noise; dropping it would lose a hypo notification if the acknowledge then
 * fails. So it WAITS, and fires only when the last acknowledge of that alert
 * ends without the record holding it.
 */
const inFlight = new Map<AlertId, number>()
const owedNotification = new Map<AlertId, () => void>()

/** A surface is sending an acknowledge of `alertId`. Pair with `endAcknowledge`. */
export const beginAcknowledge = (alertId: AlertId): void => {
  inFlight.set(alertId, (inFlight.get(alertId) ?? 0) + 1)
}

/**
 * That acknowledge ended (success, refusal or error). Call it AFTER
 * `announceAlertAcknowledged` on success, so a success is already recorded.
 */
export const endAcknowledge = (alertId: AlertId): void => {
  const left = (inFlight.get(alertId) ?? 0) - 1
  if (left > 0) {
    inFlight.set(alertId, left)
    return
  }
  inFlight.delete(alertId)
  const notify = owedNotification.get(alertId)
  owedNotification.delete(alertId)
  if (notify && !known.has(alertId)) notify()
}

export const isAcknowledgeInFlight = (alertId: AlertId): boolean => inFlight.has(alertId)

/** Show `notify` only if the in-flight acknowledge of `alertId` does not succeed. */
export const notifyUnlessAcknowledged = (alertId: AlertId, notify: () => void): void => {
  owedNotification.set(alertId, notify)
}

/**
 * The record belongs to ONE session: sign-out and sign-in clear it, since
 * neither reloads the page (eng-brain P69-5).
 */
export const clearKnownAcks = (): void => {
  known.clear()
  // A notification owed to the previous session is not the next account's.
  // In-flight counts stay: their requests still end and call endAcknowledge.
  owedNotification.clear()
}

export const __resetKnownAcksForTests = (): void => {
  clearKnownAcks()
  inFlight.clear()
}
